import React, { useEffect, useMemo, useState } from "react";
import { getCiMarketPosition, getCiRateCalendar } from "../../api/competitiveIntelligence";
import { RefetchBar } from "./AnalysisPanel";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import PanelErrorBoundary, { sanitizePanelError } from "../shared/PanelErrorBoundary";
import { fmtOcc, categoryLabel } from "../../utils/ciFormat";
import ThinScroll from "../shared/ThinScroll";
import { SkeletonText } from "../shared/Skeleton";
import SelectMenu from "../shared/SelectMenu";
import { fmtRate, fmtDateShort, ExportMenu, downloadCsv, downloadPdf } from "./shared";
// Item 3 — competitor-group (p_tier) selector. The CI shared barrel
// (shared/components.js) re-exports PanelTakeaway + VerdictBlock but not
// GroupSelect, so import it directly from its module.
import GroupSelect from "./GroupSelect";

// API now returns a server-computed `display_label` per row (e.g. "May 1 → May 3 · 2 nights",
// "Memorial Day Weekend · May 22 → May 25 · 3 nights"). Use it directly; fall back to the raw
// date if the field is missing for any reason.
const fmtDateLabel = (opt) => {
  if (!opt) return "—";
  return opt.display_label || fmtDateShort(opt.check_in);
};

// Compact label for the <option> list. Native <select> popup width tracks the
// widest option text, so the full display_label ("May 22 → May 25 · 3 nights ·
// Memorial Day Weekend") makes the popup overflow narrow viewports. Drop the
// "→ end_date" range (nights conveys span) so holiday rows still fit.
const fmtDateOption = (opt) => {
  if (!opt) return "—";
  const base = fmtDateShort(opt.check_in);
  const nights = opt.nights ? ` · ${opt.nights} night${opt.nights === 1 ? "" : "s"}` : "";
  const holiday = opt.date_window_type === "holiday" && opt.date_window_name
    ? ` · ${opt.date_window_name}`
    : "";
  return `${base}${nights}${holiday}`;
};

const fmtRateRound = (value) => (value == null ? "—" : `$${Math.round(Number(value))}`);

// Tier-coloured bar fills. "client" gets brand blue + ring; we keep hotel a softer sky tone so it
// doesn't visually compete with the highlighted client bar.
// Tier colors follow the spec palette (Direct=emerald #10B981, Hotel=blue #3B82F6,
// Airbnb=pink #EC4899). Lightened/desaturated so they don't compete with the
// highlighted client bar above.
const TIER_BAR = {
  client: "bg-[#32B1FF]",
  direct: "bg-emerald-500/80 dark:bg-emerald-500/70",
  hotel: "bg-blue-400/80 dark:bg-blue-500/60",
  airbnb: "bg-pink-400/80 dark:bg-pink-500/60",
};

const TIER_LABEL = {
  client: "You",
  direct: "Direct",
  hotel: "Hotel",
  airbnb: "Airbnb",
};

// Display order for tier tabs. Tabs themselves are built from the tiers present in
// the data; this only fixes their left-to-right order. "client" is the user's own
// property, never a selectable comp tier, so it's excluded when building tabs.
const TIER_ORDER = ["direct", "hotel", "airbnb"];

// Build the tier tab list ([All, ...present comp tiers]) from a set of tier codes
// found in the rankings. Known tiers come first in TIER_ORDER; any unknown tier is
// appended so we never silently drop a tier the backend introduces later.
const buildTierTabs = (tierSet) => {
  const known = TIER_ORDER.filter((tier) => tierSet.has(tier));
  const extras = Array.from(tierSet).filter((tier) => !TIER_ORDER.includes(tier) && tier !== "client");
  return [
    { value: null, label: "All" },
    ...[...known, ...extras].map((tier) => ({ value: tier, label: TIER_LABEL[tier] || tier })),
  ];
};

const initialState = { loading: false, error: "", data: null };

// API now returns `occupancy_unavailable_reason` whenever `inferred_occupancy_pct` is null.
// Map each reason code to a human tooltip so the "—" badge tells the user why.
const OCC_REASON_TOOLTIP = {
  no_pms_data: "No PMS data source — occupancy isn't tracked for hotels and Airbnb listings.",
  insufficient_history: "Not enough sync cycles yet to estimate occupancy.",
};

const HeaderRow = ({ activeLabel, actions = null }) => (
  <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3 flex-wrap">
    <div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
        Market Position
        <InfoTip text="Horizontal bar chart ranking every property by nightly rate for the selected date. Your bar is highlighted so you can see exactly where you sit in the pack." />
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        How your rate ranks against the competitive set on a given date.
      </p>
    </div>
    <div className="flex items-center gap-3">
      {activeLabel && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Showing: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeLabel}</span>
        </div>
      )}
      {actions}
    </div>
  </div>
);

const SkeletonBars = () => (
  <div className="px-5 py-5">
    <SkeletonText lines={6} lineClass="h-9" />
  </div>
);

// Export column spec — drives both CSV and PDF. The on-screen bars don't translate
// to a flat export, so we emit the underlying ranking values. Occupancy uses fmtOcc
// (leading ~) to match how it renders in the panel.
const EXPORT_COLUMNS = [
  { header: "Rank", get: (row, index) => String(index + 1) },
  { header: "Property", get: (row) => row.property_name || "" },
  { header: "Tier", get: (row) => (row.is_client ? "You" : (TIER_LABEL[row.tier] || row.tier || "")) },
  { header: "Nightly Rate", get: (row) => fmtRate(row.nightly_rate) },
  { header: "Inferred Occupancy", get: (row) => fmtOcc(row.inferred_occupancy_pct) },
];

const EXPORT_HEADERS = EXPORT_COLUMNS.map((column) => column.header);
const toExportRow = (row, index) => EXPORT_COLUMNS.map((column) => column.get(row, index));

// Drop duplicate property rows the API can return when a property exposes multiple site types in
// the same tier — keep the highest rate per (property, tier).
const dedupeRankings = (rankings) => {
  const map = new Map();
  for (const row of rankings) {
    const key = `${row.property_name}::${row.tier}`;
    const prev = map.get(key);
    if (!prev || (row.nightly_rate ?? 0) > (prev.nightly_rate ?? 0)) {
      map.set(key, row);
    }
  }
  return Array.from(map.values());
};

// Item 3 — default competitor group (p_tier) for the active category. "direct"
// (Your competitors) when the category exposes a Direct tier, otherwise the
// first tier in the spec display order (airbnb → hotel → glampinghub → public).
// Mirrors GroupSelect's own option ordering so the default always maps to a
// visible option.
const GROUP_DEFAULT_ORDER = ["direct", "airbnb", "hotel", "glampinghub", "public"];
const defaultGroupFor = (categoryDetails) => {
  const byTier = categoryDetails?.properties_by_tier;
  if (!byTier) return null;
  const tiers = Array.isArray(byTier)
    ? byTier.map((row) => row?.tier).filter(Boolean)
    : Object.keys(byTier);
  for (const tier of GROUP_DEFAULT_ORDER) {
    if (tiers.includes(tier)) return tier;
  }
  return tiers[0] ?? null;
};

const MarketPositionPanel = ({ clientId, categoryCode = null, availableCategories = [], categoryDetails }) => {
  // Tier filter for the bar chart. We fetch every tier once (tier=null) and filter
  // client-side, so this drives a local filter, not a refetch. Starts on "direct"
  // to preserve the old default intent (focus on true comps when available); the
  // coercion effect falls back to "All" for categories without a Direct tier. The
  // actual tier tabs are derived from the tiers present in the data (`tierTabs`).
  const [tier, setTier] = useState("direct");
  const [showOccupancy, setShowOccupancy] = useState(true);
  const [state, setState] = useState(initialState);
  // User-selected check-in date. When null, the panel auto-picks the first available date.
  const [pickedDate, setPickedDate] = useState(null);
  // Own rate calendar fetch keyed on the active category so the available dates stay
  // populated even when the page-level filter is "All categories".
  const [calendarState, setCalendarState] = useState(initialState);
  // Item 3 — competitor group (p_tier). The panel owns its own fetch, so we
  // pass `tier: group` straight to getCiMarketPosition and refetch on change.
  // Default: "direct" (Your competitors) when the active category has it,
  // otherwise the first tier in display order (airbnb → hotel → glampinghub →
  // public). GroupSelect stays visible even with a single option; it self-hides
  // only when the category exposes no tiers.
  const [group, setGroup] = useState(null);
  useEffect(() => {
    setGroup(defaultGroupFor(categoryDetails));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryDetails]);
  const handleGroupChange = (value) => setGroup(value);

  // The Market Position RPC requires a specific category. We mirror the page filter
  // when it names one. On "All categories" we auto-pick — but rather than a hardcoded
  // guess (which used to be Lodging, often empty before its pipeline shipped), the
  // probe effect below chooses the category with the most forward rate data. The user
  // can still override via the in-panel "View" pills.
  const [effectiveCategory, setEffectiveCategory] = useState(categoryCode || null);

  // Mirrors CompetitiveIntelligencePage's usableCategories probe. The page passes
  // the RAW `availableCategories` (every category the client "operates"), which
  // includes dead tabs: Tent (competitor rows exist but every row's client_rate is
  // null — no client inventory) and Lodging (no future check-in dates at all).
  // Market Position requires a single category, so we re-derive usability here and
  // only render categories that actually carry client rate data. A category is
  // usable iff at least one row in the null-category calendar payload carries a
  // non-null `client_rate` for its `category_code`. Runs once per
  // (client, category-set); does NOT re-run on tab switch.
  const [categoryProbe, setCategoryProbe] = useState({ loading: false, usable: {} });
  useEffect(() => {
    if (!clientId || !availableCategories.length) return;
    let cancelled = false;
    setCategoryProbe({ loading: true, usable: {} });
    getCiRateCalendar(clientId, { categoryCode: null })
      .then((resp) => {
        if (cancelled) return;
        const payload = resp?.data;
        const items = Array.isArray(payload) ? payload : (payload?.items ?? []);
        const usable = {};
        for (const code of availableCategories) usable[code] = false;
        for (const row of items) {
          if (row.category_code && row.client_rate != null) {
            usable[row.category_code] = true;
          }
        }
        setCategoryProbe({ loading: false, usable });
      })
      .catch(() => {
        if (cancelled) return;
        // On probe failure, fall back to showing every category rather than
        // silently hide one that may have data (e.g. a transient network blip).
        const usable = {};
        for (const code of availableCategories) usable[code] = true;
        setCategoryProbe({ loading: false, usable });
      });
    return () => { cancelled = true; };
  }, [clientId, availableCategories]);

  // Categories that survived the usability probe — pills, validation, the auto-pick,
  // and the "try another category" hint all operate on this filtered list so dead
  // categories (Tent, Lodging) never appear. Empty while the probe is in flight.
  const usableCategories = useMemo(
    () => availableCategories.filter((code) => categoryProbe.usable[code] === true),
    [availableCategories, categoryProbe.usable]
  );

  // Sync to the page filter when it names a category. On "All categories", keep the
  // current pick if it's still usable; otherwise clear it so the auto-pick re-runs.
  // When the page filter names a category the probe has ruled unusable (e.g. Tent
  // with no client inventory), fall back to the first usable category instead of
  // rendering a dead view. Gated on categoryProbe.loading so we don't reset during
  // the in-flight window when usableCategories is still empty.
  useEffect(() => {
    if (categoryCode) {
      if (!categoryProbe.loading && usableCategories.length && !usableCategories.includes(categoryCode)) {
        setEffectiveCategory(usableCategories[0]);
      } else {
        setEffectiveCategory(categoryCode);
      }
      return;
    }
    if (categoryProbe.loading) return;
    if (effectiveCategory && usableCategories.includes(effectiveCategory)) return;
    setEffectiveCategory(null);
  }, [categoryCode, categoryProbe.loading, usableCategories, effectiveCategory]);

  // Auto-pick a default category when the page filter is "All categories". With
  // unusable categories already filtered out, the first usable category is the
  // default (RV when present). Runs after the probe resolves; keeps a user's
  // manual pick if it survived the filter.
  useEffect(() => {
    if (categoryCode) return;
    if (categoryProbe.loading) return;
    if (!usableCategories.length) return;
    if (effectiveCategory && usableCategories.includes(effectiveCategory)) return;
    setEffectiveCategory(usableCategories[0]);
  }, [categoryCode, categoryProbe.loading, usableCategories, effectiveCategory]);

  useEffect(() => {
    if (!clientId || !effectiveCategory) {
      setCalendarState(initialState);
      return;
    }
    let cancelled = false;
    // Keep the previous `data` in place while the new category's rows are in flight so the
    // date dropdown doesn't briefly empty + collapse on every category switch. The new data
    // overwrites this once the request resolves.
    setCalendarState((prev) => ({ loading: true, error: "", data: prev.data }));
    // Request a large limit so we get the full 90-day window in one call — this panel
    // computes derived stats over the entire dataset, not a single page.
    getCiRateCalendar(clientId, { categoryCode: effectiveCategory, page: 1, limit: 1000 })
      .then((resp) => {
        const payload = resp?.data;
        const items = Array.isArray(payload) ? payload : (payload?.items ?? []);
        if (!cancelled) setCalendarState({ loading: false, error: "", data: items });
      })
      .catch((err) => {
        if (!cancelled) setCalendarState({ loading: false, error: err?.message || "Failed to load dates", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, effectiveCategory]);

  // Distinct check-in dates for the panel's category. Mirrors RateCalendarPanel which shows the
  // full forward-looking 90-day window — navbar date range is ignored for CI rate views.
  const dateOptions = useMemo(() => {
    const rows = Array.isArray(calendarState.data) ? calendarState.data : [];
    const seen = new Map();
    for (const row of rows) {
      if (!row?.check_in) continue;
      if (!seen.has(row.check_in)) {
        seen.set(row.check_in, {
          check_in: row.check_in,
          display_label: row.display_label || null,
          nights: row.nights,
          date_window_type: row.date_window_type,
          date_window_name: row.date_window_name,
        });
      }
    }
    return Array.from(seen.values()).sort((dateA, dateB) => dateA.check_in.localeCompare(dateB.check_in));
  }, [calendarState.data]);

  // Reset the picked date if it falls outside the available options (e.g. category switch).
  useEffect(() => {
    if (!pickedDate) return;
    if (!dateOptions.some((dateOption) => dateOption.check_in === pickedDate)) setPickedDate(null);
  }, [dateOptions, pickedDate]);

  const activeDate = useMemo(() => {
    if (pickedDate) {
      return dateOptions.find((dateOption) => dateOption.check_in === pickedDate) || null;
    }
    return dateOptions[0] || null;
  }, [pickedDate, dateOptions]);

  const selectedDate = activeDate?.check_in ?? null;
  const activeLabel = activeDate ? fmtDateLabel(activeDate) : null;

  useEffect(() => {
    if (!clientId || !selectedDate || !effectiveCategory) {
      setState(initialState);
      return;
    }
    let cancelled = false;
    // The selected competitor group (p_tier) is passed to getCiMarketPosition so
    // the ranking re-scopes to that tier; switching groups refetches (see deps).
    // The in-panel Tier pills still filter the returned set client-side, so
    // switching those is instant. Keep prior `data` visible during refetch so
    // changing date/category/group doesn't flash a skeleton — RefetchBar + dim
    // body conveys the in-flight state. First load still falls through to the
    // skeleton because `data` starts null.
    setState((prev) => ({ loading: true, error: "", data: prev.data }));
    getCiMarketPosition(clientId, { checkIn: selectedDate, categoryCode: effectiveCategory, tier: group })
      .then((resp) => {
        if (!cancelled) setState({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err?.message || "Failed to load market position", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, selectedDate, effectiveCategory, group]);

  // Full ranking set across ALL tiers, rate-sorted desc so the chart reads as a true
  // "ranked" list. We fetch tier=null and filter client-side (see `sortedRankings`),
  // so the tier tabs can reflect real data.
  const allRankings = useMemo(() => {
    const raw = state.data?.rankings || [];
    return dedupeRankings(raw).sort((rowA, rowB) => (rowB.nightly_rate ?? 0) - (rowA.nightly_rate ?? 0));
  }, [state.data]);

  // Tier tabs derived from the comp tiers actually present in the data (the client's
  // own row excluded). Always offers "All"; guarantees every tab has data behind it.
  const tierTabs = useMemo(() => {
    const set = new Set();
    for (const row of allRankings) {
      if (row.is_client || !row.tier) continue;
      set.add(row.tier);
    }
    return buildTierTabs(set);
  }, [allRankings]);

  // Coerce the selected tier when the active data doesn't offer it (e.g. RV→Lodging
  // while "direct" was selected). Skip while no comp tiers are known yet (pre-fetch),
  // so the initial "direct" default survives until the real tabs load. Fall back to
  // Direct when present (RV), else All (Lodging and anything without a Direct tier).
  useEffect(() => {
    if (tierTabs.length <= 1) return;
    const values = new Set(tierTabs.map((tab) => tab.value));
    setTier((current) => (values.has(current) ? current : values.has("direct") ? "direct" : null));
  }, [tierTabs]);

  // Bars to render: the full set filtered to the selected tier. The client row is
  // always kept so the "You rank #X" context survives any tier filter.
  const sortedRankings = useMemo(() => {
    if (!tier) return allRankings;
    return allRankings.filter((row) => row.is_client || row.tier === tier);
  }, [allRankings, tier]);

  const maxRate = useMemo(() => {
    if (!sortedRankings.length) return 0;
    return Math.max(...sortedRankings.map((row) => row.nightly_rate ?? 0), 1);
  }, [sortedRankings]);

  // Client's rank in the rate-sorted list (1-indexed). Chart sorts high → low,
  // so rank #1 = highest-priced. Drives the "You rank #X of Y" banner above
  // the chart — turns the wall of bars into a one-line ranking glance.
  const positionInfo = useMemo(() => {
    const clientIndex = sortedRankings.findIndex((row) => row.is_client);
    if (clientIndex < 0) return null;
    const total = sortedRankings.length;
    if (total < 2) return null;
    return { rank: clientIndex + 1, total, above: clientIndex, below: total - clientIndex - 1 };
  }, [sortedRankings]);

  // Comp avg / median / count for the summary strip, computed from the competitors
  // currently shown (tier-filtered, client excluded). We recompute client-side rather
  // than trust the API `summary` because we fetch every tier once and filter locally —
  // the API summary always reflects "all tiers", not the selected one.
  const summary = useMemo(() => {
    const apiSummary = state.data?.summary || {};
    const competitors = sortedRankings.filter((row) => !row.is_client);
    if (competitors.length === 0) return apiSummary;
    const rates = competitors.map((row) => row.nightly_rate).filter((rate) => rate != null).sort((rateA, rateB) => rateA - rateB);
    const avg = rates.length ? rates.reduce((sum, rate) => sum + rate, 0) / rates.length : null;
    const median = rates.length
      ? (rates.length % 2 === 1 ? rates[(rates.length - 1) / 2] : (rates[rates.length / 2 - 1] + rates[rates.length / 2]) / 2)
      : null;
    return {
      ...apiSummary,
      comp_avg: avg,
      comp_median: median,
      total_properties: competitors.length,
    };
  }, [state.data, sortedRankings]);

  // Need a resolved category before the RPC fires; only true when the client has no
  // categories at all (availableCategories is empty).
  const needsCategory = !effectiveCategory;
  // True when we auto-picked because the page filter is "All categories". Used to surface a
  // small "Showing X — change above" hint so the user knows why they're looking at one slice.
  const isFallbackActive = !categoryCode && !!effectiveCategory;

  // Export the ranked set as currently shown (category + tier + date filtered).
  const canExport = !needsCategory && !state.error && sortedRankings.length > 0;
  const exportSubtitle = [categoryLabel(effectiveCategory), activeLabel].filter(Boolean).join(" · ");
  const handleExportCsv = () => {
    downloadCsv(EXPORT_HEADERS, sortedRankings.map(toExportRow), "market-position.csv");
  };
  const handleExportPdf = () => {
    downloadPdf(
      "Market Position",
      EXPORT_HEADERS,
      sortedRankings.map(toExportRow),
      "market-position.pdf",
      exportSubtitle || undefined,
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden min-h-[400px] md:min-h-[500px] lg:h-[720px] flex flex-col">
      <HeaderRow
        activeLabel={activeLabel}
        actions={(
          <div className="flex items-center gap-3 flex-wrap">
            {/* Competitor group (p_tier) — drives getCiMarketPosition via
                `tier: group`; refetches on change (see the fetch effect deps).
                Self-hides when the active category exposes no tiers, but stays
                visible with a single option. */}
            <GroupSelect categoryDetails={categoryDetails} selectedGroup={group} onChange={handleGroupChange} />
            {canExport ? <ExportMenu onCsv={handleExportCsv} onPdf={handleExportPdf} /> : null}
          </div>
        )}
      />

      {/* Controls strip — View + Tier (pill filters) share the top row with the
          occupancy toggle pushed right; Date sits on its own row below so its
          dropdown has room and the Tier pills can grow as more comps are scraped. */}
      <div className="px-5 py-2.5 border-b border-gray-200 dark:border-gray-800 space-y-1.5">
        {/* Row 1: View + Tier + Show occupancy */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {usableCategories.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <span className="-ml-1.5 mr-1.5 inline-flex">
                  <InfoTip
                    text="This section requires a single category. Follows the page filter when it names a category; pick one here when the page filter is All."
                    placement="bottom"
                    align="start"
                  />
                </span>
                View:
              </span>
              <div className="flex items-center gap-1.5">
                {usableCategories.map((category) => {
                  const active = effectiveCategory === category;
                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setEffectiveCategory(category)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        active
                          ? "border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF]"
                          : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      {categoryLabel(category)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tier — hidden only when no comp tiers are present (just the client /
              no competitors), where there's nothing to filter. */}
          {tierTabs.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Tier:</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {tierTabs.map((tab) => {
                  const active = tier === tab.value;
                  return (
                    <button
                      key={tab.value ?? "__all__"}
                      type="button"
                      onClick={() => setTier(tab.value)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        active
                          ? "border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF]"
                          : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <label className="ml-auto inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOccupancy}
              onChange={(event) => setShowOccupancy(event.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-[#32B1FF] focus:ring-[#32B1FF]"
            />
            Show occupancy
          </label>
        </div>

        {/* Row 2: Date */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Date:</span>
          <SelectMenu
            ariaLabel="Check-in date"
            value={selectedDate}
            onChange={setPickedDate}
            disabled={dateOptions.length === 0}
            placeholder="No dates available"
            stableWidth
            options={dateOptions.map((dateOption) => ({ value: dateOption.check_in, label: fmtDateOption(dateOption) }))}
          />
        </div>
      </div>

      {/* Insight bar — rank + comp stats on one line. (Not in the v1 spec, which
          specifies only the chart + date selector + occupancy toggle; kept as a useful
          glance.) Contextual notes live in the muted panel footer instead. */}
      {!needsCategory && state.data && !state.error && (
        <div className="px-5 py-2.5 bg-gray-50 dark:bg-gray-950/40 border-b border-gray-200 dark:border-gray-800 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          {positionInfo && (
            <span className="inline-flex items-baseline gap-x-2">
              <span className="text-sm font-semibold text-[#32B1FF]">
                You rank #{positionInfo.rank} of {positionInfo.total}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {positionInfo.above === 0
                  ? "Highest-priced"
                  : positionInfo.below === 0
                  ? "Lowest-priced"
                  : `${positionInfo.above} higher · ${positionInfo.below} lower`}
              </span>
            </span>
          )}
          <span className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300 ml-auto">
            <span>
              <span className="text-gray-500 dark:text-gray-400">Comp avg</span>{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-100">{fmtRate(summary.comp_avg)}</span>
            </span>
            <span>
              <span className="text-gray-500 dark:text-gray-400">Median</span>{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-100">{fmtRate(summary.comp_median)}</span>
            </span>
            <span>
              <span className="text-gray-500 dark:text-gray-400">Properties</span>{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-100">{summary.total_properties ?? sortedRankings.length}</span>
            </span>
          </span>
        </div>
      )}

      {/* Body */}
      {needsCategory ? (
        categoryProbe.loading ? (
          <SkeletonBars />
        ) : (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 space-y-1">
            <div className="font-medium text-gray-700 dark:text-gray-200">No competitive data yet</div>
            <div className="text-xs">No accommodation categories have been synced for this client. Data appears after the first nightly sync completes.</div>
          </div>
        )
      ) : calendarState.loading && calendarState.data == null ? (
        <SkeletonBars />
      ) : calendarState.error ? (
        <div className="p-8 text-center text-sm text-red-600 dark:text-red-400 space-y-1">
          <div className="font-medium">Couldn't load calendar dates</div>
          <div className="text-xs">{sanitizePanelError(calendarState.error)}</div>
        </div>
      ) : !selectedDate ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-700 dark:text-gray-200">
            No upcoming dates in {categoryLabel(effectiveCategory)}
          </div>
          <div className="text-xs">
            No rate data has been collected for {categoryLabel(effectiveCategory)} in the next 90 days.
            {usableCategories.filter((category) => category !== effectiveCategory).length > 0 && (
              <>
                {" "}Try{" "}
                {usableCategories
                  .filter((category) => category !== effectiveCategory)
                  .map((category, index, arr) => (
                    <React.Fragment key={category}>
                      <button
                        type="button"
                        onClick={() => setEffectiveCategory(category)}
                        className="text-[#32B1FF] hover:underline font-medium"
                      >
                        {categoryLabel(category)}
                      </button>
                      {index < arr.length - 1 && " or "}
                    </React.Fragment>
                  ))}
                {" "}instead.
              </>
            )}
          </div>
        </div>
      ) : state.loading && state.data == null ? (
        <SkeletonBars />
      ) : state.error ? (
        <ErrorState variant="inset" message={sanitizePanelError(state.error)} />
      ) : !sortedRankings.length ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-700 dark:text-gray-200">
            No {tier ? (TIER_LABEL[tier] || tier).toLowerCase() : ""} competitors for {activeLabel || "this date"}
          </div>
          <div className="text-xs">
            {tier
              ? <>No <span className="font-medium">{TIER_LABEL[tier] || tier}</span> rate data is available for this date in {categoryLabel(effectiveCategory)}. </>
              : <>No competitor rate data is available for this date in {categoryLabel(effectiveCategory)}. </>}
            {tier && (
              <button
                type="button"
                onClick={() => setTier(null)}
                className="text-[#32B1FF] hover:underline font-medium"
              >
                Show all tiers
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="relative flex-1 flex flex-col min-h-0">
          {state.loading && <RefetchBar />}
          <ThinScroll className={`px-5 py-4 space-y-2 flex-1 overflow-y-auto ${state.loading ? "opacity-60 pointer-events-none transition-opacity" : "transition-opacity"}`}
            aria-busy={state.loading}>
          {sortedRankings.map((row, index) => {
            const pct = maxRate > 0 ? Math.max(((row.nightly_rate ?? 0) / maxRate) * 100, 2) : 0;
            const fill = TIER_BAR[row.tier] ?? "bg-gray-300 dark:bg-gray-700";
            const isClient = row.is_client;
            return (
              <div
                key={`${row.property_name}-${row.tier}-${index}`}
                className={`grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] items-center gap-3 ${
                  isClient ? "rounded-md ring-2 ring-[#32B1FF] bg-[#32B1FF]/[0.04] dark:bg-[#32B1FF]/[0.08] px-2 py-1.5" : "px-2 py-1.5"
                }`}
              >
                {/* Property + tier label */}
                <div className="min-w-0">
                  <div
                    className={`text-sm truncate ${isClient ? "font-bold text-gray-900 dark:text-gray-100" : "text-gray-800 dark:text-gray-200"}`}
                    title={row.property_name}
                  >
                    {row.property_name}
                    {isClient && (
                      <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wide bg-[#32B1FF] text-white rounded px-1.5 py-0.5 align-middle">
                        You
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">{TIER_LABEL[row.tier] ?? row.tier}</div>
                </div>

                {/* Bar */}
                <div className="relative h-7 bg-gray-100 dark:bg-gray-800/60 rounded">
                  <div
                    className={`absolute inset-y-0 left-0 rounded ${fill} transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                  {showOccupancy && (
                    <span
                      className="absolute inset-y-0 right-2 flex items-center text-[11px] font-semibold text-gray-700 dark:text-gray-200"
                      title={row.inferred_occupancy_pct != null
                        ? "Estimated from booking platform inventory data."
                        : OCC_REASON_TOOLTIP[row.occupancy_unavailable_reason] || "Occupancy not available for this tier."}
                    >
                      {row.inferred_occupancy_pct != null ? fmtOcc(row.inferred_occupancy_pct) : "—"}
                    </span>
                  )}
                </div>

                {/* Rate */}
                <div className={`text-sm tabular-nums whitespace-nowrap ${isClient ? "font-bold text-gray-900 dark:text-gray-100" : "text-gray-800 dark:text-gray-200"}`}>
                  {fmtRateRound(row.nightly_rate)}
                </div>
              </div>
            );
          })}
          </ThinScroll>
        </div>
      )}

      {/* Muted footer — contextual notes (auto-picked category, sparse comp set) tucked
          here so they don't clutter the top. Not part of the v1 spec. */}
      {!needsCategory && state.data && !state.error &&
        (isFallbackActive || (summary.total_properties != null && summary.total_properties <= 3)) && (
        <div className="px-5 py-1.5 border-t border-gray-200 dark:border-gray-800 text-[11px] text-gray-400 dark:text-gray-500">
          {isFallbackActive &&
            `Showing ${categoryLabel(effectiveCategory)} (most rate data) — change via the page filter or pick another above.`}
          {isFallbackActive && summary.total_properties != null && summary.total_properties <= 3 ? " " : ""}
          {summary.total_properties != null &&
            summary.total_properties <= 3 &&
            `Showing ${summary.total_properties} configured competitor${summary.total_properties === 1 ? "" : "s"}; more will be added.`}
        </div>
      )}
    </div>
  );
};

const MarketPositionPanelWithBoundary = (props) => (
  <PanelErrorBoundary label="MarketPosition">
    <MarketPositionPanel {...props} />
  </PanelErrorBoundary>
);

export default MarketPositionPanelWithBoundary;
